import type {
  StorefrontListingSort,
  StorefrontProductSummary,
} from '@/features/storefront/api/storefront';
import type { ProductTabBlock } from '@/features/storefront/sections/schema';

/**
 * What one product tab asks the storefront listing for.
 *
 * Every tab is one ordinary listing request - a sort, the `on_sale` filter or
 * a category - so a tab row reuses the product grid's endpoint and cards rather
 * than growing a second product API.
 */
export interface ProductTabQuery {
  sort: StorefrontListingSort;
  onSale: boolean;
  categoryId: string | null;
  size: number;
}

/** A React Query description of one tab's request: its key and how to fetch it. */
export interface ProductQueryOptions {
  queryKey: readonly unknown[];
  queryFn: () => Promise<StorefrontProductSummary[]>;
}

/** Builds a tab's query against whichever shop the page is rendered for. */
export type ProductQueryFactory = (query: ProductTabQuery) => ProductQueryOptions;

/**
 * The request behind one tab, or `null` for a tab that cannot ask for anything
 * yet - a category tab whose category the merchant has not picked.
 */
export function productTabQuery(tab: ProductTabBlock, size: number): ProductTabQuery | null {
  switch (tab.source) {
    case 'best_selling':
      return { sort: 'best_selling', onSale: false, categoryId: null, size };
    case 'on_sale':
      return { sort: 'newest', onSale: true, categoryId: null, size };
    case 'category':
      return tab.category_id ? { sort: 'newest', onSale: false, categoryId: tab.category_id, size } : null;
    default:
      return { sort: 'newest', onSale: false, categoryId: null, size };
  }
}

/** The cache key for a tab's request, under the shop's own scope. */
export function productTabKey(scope: readonly unknown[], query: ProductTabQuery): unknown[] {
  return [
    ...scope,
    'products',
    'tab',
    query.sort,
    query.onSale ? 'on_sale' : 'all',
    query.categoryId ?? 'all',
    query.size,
  ];
}

/** Whether a product is marked down - what "On sale" means without a server to ask. */
export function isOnSale(product: StorefrontProductSummary): boolean {
  return product.compare_at_price !== null && Number(product.compare_at_price) > Number(product.price);
}

/** The id of one tab's button, which the panel is labelled by. */
export function productTabId(sectionId: string, tabId: string): string {
  return `product-tabs-${sectionId}-${tabId}`;
}

/** The id of the one panel every tab of a section controls. */
export function productTabPanelId(sectionId: string): string {
  return `product-tabs-${sectionId}-panel`;
}
